import api from "./axiosInstance";

export const fetchUsersApi = (params: {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: "asc" | "desc";
  search: string;
}) =>
  api.get("/admin/users", {
    params: params,
  });

export const fetchInstructorsApi = (params: {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: "asc" | "desc";
  search: string;
}) =>
  api.get("/admin/instructors", {
    params: params,
  });

export const createUserApi = (userData: any) =>
  api.post("/admin/users", userData);

export const createInstructorApi = (instructorData: any, language: string) =>
  api.post(`/admin/instructors?language=${language}`, instructorData);

export const deleteUserApi = (userId: number) =>
  api.delete(`/admin/users/${userId}`);

export const deleteInstructorApi = (instructorId: number) =>
  api.delete(`/admin/instructors/${instructorId}`);

export const getUserApi = (userId: number) => api.get(`/admin/users/${userId}`);

export const getInstructorApi = (instructorId: number) =>
  api.get(`/admin/instructors/${instructorId}`);

export const updateUserApi = (userId: number, userData: any) =>
  api.patch(`/admin/users/${userId}`, userData);

export const updateInstructorApi = (
  instructorId: number,
  instructorData: any
) => api.patch(`/admin/instructors/${instructorId}`, instructorData);

export const resetUserPasswordApi = (userId: number, language: string) =>
  api.post(`/admin/users/${userId}/reset-password?language=${language}`);

export const resetInstructorPasswordApi = (
  instructorId: number,
  language: string
) =>
  api.post(
    `/admin/instructors/${instructorId}/reset-password?language=${language}`
  );

export const uploadClassImagesApi = (classId: number, formData: FormData) =>
  api.post(`/upload/images/${classId}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

export const deleteClassImageApi = (classId: number, imageUrl: string) =>
  api.delete(`/upload/images/${classId}`, {
    params: { imageUrl },
  });

export const uploadTypeImageApi = (typeId: number, formData: FormData) =>
  api.post(`/upload/type-image/${typeId}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

export const deleteTypeImageApi = (typeId: number, imageUrl: string) =>
  api.delete(`/upload/type-image/${typeId}`, {
    params: { imageUrl },
  });

export const uploadSubtypeImageApi = (subtypeId: number, formData: FormData) =>
  api.post(`/upload/subtype-image/${subtypeId}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

export const deleteSubtypeImageApi = (subtypeId: number, imageUrl: string) =>
  api.delete(`/upload/subtype-image/${subtypeId}`, {
    params: { imageUrl },
  });

export const setClassInProgressOfCancellationApi = (
  classId: number,
  cancelDate: string,
  language: string
) =>
  api.patch(`/admin/classes/${classId}/cancel?language=${language}`, {
    cancelDate,
  });

export const reactivateClassApi = (classId: number) =>
  api.patch(`/admin/classes/${classId}/reactivate`);

export const getClassReportApi = (params: {
  instructorId?: number;
  classId?: number;
  fromDate?: string;
  toDate?: string;
  typeId?: number;
  locationId?: number;
}) => api.get("/reports/classes", { params });

export const getInstructorClassHoursReportApi = (params: {
  instructorId?: string;
  classId?: string;
  typeId?: string;
  fromDate?: string;
  toDate?: string;
}) => api.get("/reports/instructor-class-hours", { params });

export const getEnrollmentsReportApi = (params: {
  instructorId?: string;
  classId?: string;
  fromDate?: string;
  toDate?: string;
  typeId?: string;
  locationId?: string;
}) => api.get("/reports/enrollments", { params });

export const archiveClassApi = (classId: number, language?: string) =>
  api.patch(`/admin/classes/${classId}/archive?language=${language}`);

export const unarchiveClassApi = (classId: number, language?: string) =>
  api.patch(`/admin/classes/${classId}/unarchive?language=${language}`);

export const getArchivedClassApi = (classId: number) =>
  api.get(`/admin/classes/${classId}/archived`);

export const fetchArchivedClassesApi = (params: {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: "asc" | "desc";
  search: string;
}) =>
  api.get("/admin/classes/archived", {
    params: params,
  });

export const createPriceApi = (data: {
  numberOfClasses: number;
  specialType?: string | null;
  amount: number;
  currency?: string;
  classId?: number | null;
  isAdminOnly?: boolean;
}) => api.post("/admin/prices", data);

export const fetchPricesApi = (searchTerm?: string) => {
  const params = searchTerm ? { search: searchTerm } : {};
  return api.get("/admin/prices", { params });
};

export const updatePriceApi = (
  id: number,
  data: Partial<{
    amount: number;
    numberOfClasses?: number | null;
    specialType?: string | null;
    currency: string;
    classId?: number | null;
    isAdminOnly?: boolean;
  }>
) => api.patch(`/admin/prices/${id}`, data);

export const deletePriceApi = (id: number) => api.delete(`/admin/prices/${id}`);

export const fetchPaymentHistoryApi = (params: {
  page: number;
  limit: number;
  attendeeId?: number;
  classId?: number;
  startDate?: string;
  endDate?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}) =>
  api.get("/admin/payment-history", {
    params: params,
  });

export const fetchPaymentStatisticsApi = (params: {
  startDate?: string;
  endDate?: string;
  attendeeId?: number;
}) =>
  api.get("/admin/payment-statistics", {
    params: params,
  });

export const fetchAttendeesApi = (params: {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: "asc" | "desc";
  search: string;
}) =>
  api.get("/admin/attendees", {
    params: params,
  });

export const fetchAttendeesForUserApi = (userId: number) => {
  return api.get(`/attendee/user/${userId}`);
};

export const processPendingEnrollmentsApi = (attendeeId: number) =>
  api.post(`/admin/process-pending-enrollments/${attendeeId}`);
